import {Section, Point, Row} from '../../../schemas';
import {flatten} from '../../utils/array';

import compositeDecoder from './composite_decoder';
import polygonDecoder from './polygon_decoder';
import labelDecoder from './label_decoder';
import seatDecoder from './seat_decoder';
import adjustmentsDecoder from './adjustments_decoder';

export default (geometryData, availabilityData) => {
  const decodedComposites = compositeDecoder(geometryData.composite, availabilityData);
  const decodedPolygon = polygonDecoder(geometryData.polygon);
  const decodedLabel = labelDecoder(geometryData.label, geometryData.labelTemplate);
  const decodedAdjustments = adjustmentsDecoder(geometryData.section);

  if (decodedComposites.length === 0) {
    console.warn(`decodedComposites returns empty for section ${geometryData.section.id}`);
  }

  // this is the final object we return
  let section = new Section({
    id:                 geometryData.section.id,
    code:               (decodedComposites[0] && decodedComposites[0].section_name) || '',
    name:               (decodedComposites[0] && decodedComposites[0].section_name.split('-')[0]) || '', // TODO, improve this
    availableSeatCount: 0,
    label:              decodedLabel,
    polygon:            decodedPolygon,
    isGA:               ((decodedComposites.length === 0) || decodedPolygon.isGA),
    angle:              +geometryData.section.a || 0,
    size:               +geometryData.section.scale / 2 || 50, // IDEA: this could be helpful for label size bugs
    // NOTE: These properties are updated later in this function
    rows:               [],
    priceCategories:    [],
    priceCategoryAvailability: {},
    position:           new Point({x: +geometryData.section.x, y: +geometryData.section.y }),
    translate:          new Point({x: 0, y: 0 })
  });

  // keep track of the minimum x,y of each seat (used to calculate translations)
  let minLocation = new Point({
    x: 1000000,
    y: 1000000
  });

  // keep track of the priceLevels
  let priceLevels = {};

  const sectioInfoForSeatDecoding = {
    scale:        +geometryData.section.scale || 100,
    skew:         +geometryData.section.starting_seat_delta || 0,
    pna:          +geometryData.section.pna || 0,
    pnp:          +geometryData.section.pnp || 0,
    dTangentialY: +geometryData.section.sye || 0,
    deltaCenter: {
      x: +geometryData.section.center_delta || 0,
      y: +geometryData.section.center_row_movement || 0
    },
    spacing: {
      seat: +geometryData.section.seat_spacing,
      row:  +geometryData.section.row_spacing
    },
    center: {
      x: +geometryData.section.center_x || 0,
      y: +geometryData.section.center_y || -2000000
    },
    adjustments: decodedAdjustments
  };

  let minSeat = 1;
  if (section.isGA) {
    minSeat = Math.min(
      ...Object.keys(availabilityData)
        .filter((seat) => seat.substr(0, section.code.length) === section.code)
        .map(seat => {
          const parsedSeat = seat.split('-');
          return +parsedSeat[parsedSeat.length - 1];
        })
    );
  }

  const rows = decodedComposites.map(line => {
    return line.rows.map((row, rowIdx) => {

      // define the row object
      const sectionRow = new Row({
        section:            section.id,
        block:              line.section_name,
        name:               row,
        availableSeatCount: 0,
        seats:              []
      });

      // create a seat object for every seat in the row
      sectionRow.seats = line.seats.map((seat, seatIdx) => {

        const seatId = line.section_name + '-' + row + '-' + seat;
        let seatAvailability = availabilityData[seatId];

        // hack, this is to make the availability work for GA with new tool
        // see https://git.tmaws.io/sim/sim-packages/issues/3
        if (!seatAvailability && section.isGA) {
          // ignore the row in the ID
          const newId = minSeat.toString() !== 'NaN' ?
            (line.section_name + '--' + (minSeat + rowIdx * line.seats.length + Number(seat) - 1)) :
            (line.section_name + '--' + seat);
          seatAvailability = availabilityData[newId];
        }

        if (!seatAvailability) return;

        // this is all the info needed to calculate the position of each seat
        const seatInfo = {
          id:      seatId,
          row:     row,
          seat:    seat,
          rowNum:  rowIdx + line.map_to_y,
          seatNum: seatIdx + line.map_to_x,
          section: section.id,
          blockingCode: seatAvailability && seatAvailability.blockingCode,
          priceLevel: seatAvailability && seatAvailability.priceLevel,
          available: seatAvailability && seatAvailability.available,
          attributes: seatAvailability && seatAvailability.attributes
        };
        // update available counters
        if (seatInfo.available) {
          sectionRow.availableSeatCount++;
          section.availableSeatCount++;
          section.isGA && seatInfo.priceLevel && (
            section.priceCategoryAvailability[seatInfo.priceLevel] = ++section.priceCategoryAvailability[seatInfo.priceLevel] || 1
          );
          priceLevels[seatInfo.priceLevel] = 1;
        }

        // calculate the single seat geometry
        const seatGeometry = seatDecoder(seatInfo, sectioInfoForSeatDecoding);

        // calculate if the min x,y changed here
        if ((seatGeometry.position.x - seatGeometry.size) < minLocation.x) {
          minLocation.x = (seatGeometry.position.x - seatGeometry.size);
        }
        if ((seatGeometry.position.y - seatGeometry.size) < minLocation.y) {
          minLocation.y = (seatGeometry.position.y - seatGeometry.size);
        }

        return seatGeometry;
      }).filter(s => !!s && s.avialble === undefined);

      // finally add the row to the section
      return sectionRow;
    });
  });

  section.rows = flatten(rows);

  // start by updating the section translation
  section.translate.x = (section.position.x - minLocation.x).toFixed(2);
  section.translate.y = (section.position.y - minLocation.y).toFixed(2);

  // now reset the position to the minLocation
  section.position.x = (minLocation.x).toFixed(2);
  section.position.y = (minLocation.y).toFixed(2);

  section.priceCategories = Object.keys(priceLevels);

  // apply attributes to the polygon object
  section.polygon.attributes = section.priceCategories.map(pc => `pc-${pc}`);

  return section;
};



// WEBPACK FOOTER //
// ./frontend/react/sim/parser-mnx-geometry/src/decoders/section_decoder.js